import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

// Sites group workers by physical location or facility — state offices ("VIC",
// "NSW") for Empower Healthcare, RAC facilities for aged-care providers, etc. They
// scope alarms via emergency-contacts: a site-scoped contact fires for any worker
// assigned to that site.

export interface SiteDto {
  id: string;
  organisation_id: string;
  name: string;
  address: string | null;
  monitoring_phone: string | null;
  timezone: string;
  // Office flag + coords drive the reimbursement report's office-bonus
  // geofence (200 m). Coords are only meaningful when is_office=true.
  is_office: boolean;
  latitude: number | null;
  longitude: number | null;
  member_count: number;
  created_at: string;
}

export interface SiteMemberDto {
  site_id: string;
  user_id: string;
  display_name: string;
  email: string;
  avatar_url: string | null;
}

export interface CreateSiteInput {
  name: string;
  address?: string | null;
  monitoring_phone?: string | null;
  timezone?: string;
  is_office?: boolean;
  latitude?: number | null;
  longitude?: number | null;
}

export interface UpdateSiteInput {
  id: string;
  name?: string;
  address?: string | null;
  monitoring_phone?: string | null;
  timezone?: string;
  is_office?: boolean;
  latitude?: number | null;
  longitude?: number | null;
}

export function useSites() {
  const api = useApi();
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => api<{ sites: SiteDto[] }>('/sites'),
    staleTime: 30_000,
  });
}

export function useSite(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['sites', id],
    queryFn: () => api<SiteDto>(`/sites/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useSiteMembers(siteId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['sites', siteId, 'members'],
    queryFn: () => api<{ members: SiteMemberDto[] }>(`/sites/${siteId}/members`),
    enabled: Boolean(siteId),
    staleTime: 30_000,
  });
}

export function useCreateSite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSiteInput) =>
      api<{ site: SiteDto }>('/sites', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useUpdateSite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateSiteInput) =>
      api<{ site: SiteDto }>(`/sites/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useDeleteSite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/sites/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useSetSiteMembers() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, userIds }: { siteId: string; userIds: string[] }) =>
      api<void>(`/sites/${siteId}/members`, {
        method: 'PUT',
        body: JSON.stringify({ user_ids: userIds }),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      qc.invalidateQueries({ queryKey: ['sites', vars.siteId, 'members'] });
    },
  });
}

export function useAddSiteMembers() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, userIds }: { siteId: string; userIds: string[] }) =>
      api<void>(`/sites/${siteId}/members`, {
        method: 'POST',
        body: JSON.stringify({ user_ids: userIds }),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      qc.invalidateQueries({ queryKey: ['sites', vars.siteId, 'members'] });
    },
  });
}

export function useRemoveSiteMember() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, userId }: { siteId: string; userId: string }) =>
      api<void>(`/sites/${siteId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      qc.invalidateQueries({ queryKey: ['sites', vars.siteId, 'members'] });
    },
  });
}
